import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Shield, Plus, Edit, Trash2, Lock, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

// Permissões disponíveis para cada funcionalidade
const PERMISSIONS_TYPES = ['view', 'edit', 'delete'];
const PERMISSIONS_LABELS = {
  view: 'Ver',
  edit: 'Editar',
  delete: 'Excluir'
};

const Profiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteProfileId, setDeleteProfileId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    permissions: {} // { dashboard: { view: true, edit: false, delete: false }, ... }
  });

  useEffect(() => {
    document.title = "Gestão de Perfis — NautilusOS";
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profilesRes, featuresRes] = await Promise.all([
        api.get('/profiles'),
        api.get('/profiles/features/grouped')
      ]);
      setProfiles(profilesRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = useCallback((name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }, []);

  const openNewProfile = () => {
    setEditingProfile(null);
    const initialPermissions = {};
    (features || []).forEach(group => {
      (group.features || []).forEach(f => {
        initialPermissions[f.id] = { view: false, edit: false, delete: false };
      });
    });
    setFormData({ name: '', slug: '', description: '', permissions: initialPermissions });
    setShowDialog(true);
  };

  const openEditProfile = (profile) => {
    setEditingProfile(profile);
    const permissions = {};
    (features || []).forEach(group => {
      (group.features || []).forEach(f => {
        const hasFeature = (profile.features || []).includes(f.id);
        permissions[f.id] = { view: hasFeature, edit: hasFeature, delete: hasFeature };
      });
    });
    if (profile.detailed_permissions) {
      Object.assign(permissions, profile.detailed_permissions);
    }
    setFormData({
      name: profile.name,
      slug: profile.slug,
      description: profile.description || '',
      permissions
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar nome
    if (!formData.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    
    // Gerar slug se necessário
    const slug = editingProfile ? formData.slug : generateSlug(formData.name);
    if (!slug) {
      toast.error('Slug inválido');
      return;
    }
    
    try {
      // Converter permissões para formato de features (para compatibilidade)
      const enabledFeatures = Object.entries(formData.permissions || {})
        .filter(([_, perms]) => perms && (perms.view || perms.edit || perms.delete))
        .map(([featureId]) => featureId);
      
      // Gerar lista de permissões detalhadas
      const permissionsList = [];
      Object.entries(formData.permissions || {}).forEach(([featureId, perms]) => {
        if (perms) {
          if (perms.view) permissionsList.push(`${featureId}:view`);
          if (perms.edit) permissionsList.push(`${featureId}:edit`);
          if (perms.delete) permissionsList.push(`${featureId}:delete`);
        }
      });
      
      const payload = {
        name: formData.name,
        slug: slug,
        description: formData.description,
        features: enabledFeatures,
        permissions: permissionsList,
        detailed_permissions: formData.permissions
      };
      
      if (editingProfile) {
        await api.put(`/profiles/${editingProfile.id}`, payload);
        toast.success('Perfil atualizado!');
      } else {
        await api.post('/profiles', payload);
        toast.success('Perfil criado!');
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao salvar';
      toast.error(typeof message === 'string' ? message : 'Erro ao salvar perfil');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/profiles/${deleteProfileId}`);
      toast.success('Perfil excluído!');
      setDeleteProfileId(null);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir';
      toast.error(typeof message === 'string' ? message : 'Erro ao excluir perfil');
    }
  };

  const togglePermission = (featureId, permType) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [featureId]: {
          ...(prev.permissions[featureId] || { view: false, edit: false, delete: false }),
          [permType]: !(prev.permissions[featureId]?.[permType] || false)
        }
      }
    }));
  };

  const toggleAllPermissions = (featureId, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [featureId]: { view: checked, edit: checked, delete: checked }
      }
    }));
  };

  if (loading) {
    return <div className="flex justify-center py-8">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <TitleWithHelp
            title="Perfis de Acesso"
            description="Defina perfis com permissões granulares (Ver, Editar, Excluir) para cada funcionalidade do sistema. Perfis de sistema não podem ser excluídos."
            connections={['Usuários']}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-600" />
              Gestão de Perfis
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Configure perfis de acesso e permissões</p>
        </div>
        <Button onClick={openNewProfile}>
          <Plus className="mr-2" size={20} />
          Novo Perfil
        </Button>
      </div>

      {/* Lista de Perfis */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {profiles.map(profile => (
          <Card key={profile.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${profile.is_system ? 'bg-amber-100' : 'bg-blue-100'}`}>
                    {profile.is_system ? (
                      <Lock size={16} className="text-amber-600" />
                    ) : (
                      <Shield size={16} className="text-blue-600" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-base">{profile.name}</CardTitle>
                    <p className="text-sm text-gray-500">@{profile.slug}</p>
                  </div>
                </div>
                {profile.is_system && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    Sistema
                  </Badge>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              {profile.description && (
                <p className="text-sm text-gray-600 mb-3">{profile.description}</p>
              )}
              
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Funcionalidades:</p>
                <div className="flex flex-wrap gap-1">
                  {(profile.features || []).slice(0, 5).map(f => {
                    let featureName = f;
                    (features || []).forEach(group => {
                      const found = (group.features || []).find(feat => feat.id === f);
                      if (found) featureName = found.name;
                    });
                    return (
                      <Badge key={f} variant="secondary" className="text-xs">
                        {featureName}
                      </Badge>
                    );
                  })}
                  {(profile.features || []).length > 5 && (
                    <Badge variant="secondary" className="text-xs">
                      +{profile.features.length - 5}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEditProfile(profile)} className="flex-1">
                  <Edit className="h-3 w-3 mr-1" />
                  Editar
                </Button>
                {!profile.is_system && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setDeleteProfileId(profile.id)}
                    className="flex-1 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Excluir
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog Criar/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProfile ? 'Editar Perfil' : 'Novo Perfil'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome do Perfil *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Supervisor de Produção"
                  required
                />
              </div>
              <div>
                <Label>Identificador (slug)</Label>
                <Input
                  value={editingProfile ? formData.slug : generateSlug(formData.name)}
                  disabled
                  className="bg-gray-50"
                />
              </div>
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva as responsabilidades deste perfil..."
                rows={2}
              />
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Label>Permissões por Funcionalidade</Label>
                <HelpTooltip 
                  title="Sistema de Permissões"
                  description="Configure acesso granular: Ver (visualizar dados), Editar (criar/modificar) e Excluir (remover). Marque a checkbox principal para dar acesso total à funcionalidade."
                  iconSize={14}
                />
              </div>
              <div className="border rounded-lg overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-5 gap-2 bg-gray-100 p-3 text-sm font-medium">
                  <div className="col-span-2">Funcionalidade</div>
                  <div className="text-center">
                    <Eye className="h-4 w-4 inline mr-1" />
                    Ver
                  </div>
                  <div className="text-center">
                    <Pencil className="h-4 w-4 inline mr-1" />
                    Editar
                  </div>
                  <div className="text-center">
                    <Trash2 className="h-4 w-4 inline mr-1" />
                    Excluir
                  </div>
                </div>
                
                {/* Rows agrupadas */}
                <div className="divide-y">
                  {(features || []).map(group => (
                    <div key={group.id}>
                      {/* Group header */}
                      <div className="bg-gray-50 px-3 py-2 border-b">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{group.name}</span>
                      </div>
                      {(group.features || []).map(feature => {
                        const perms = formData.permissions?.[feature.id] || { view: false, edit: false, delete: false };
                        const allChecked = perms.view && perms.edit && perms.delete;
                        
                        return (
                          <div key={feature.id} className="grid grid-cols-5 gap-2 p-3 items-center hover:bg-gray-50">
                            <div className="col-span-2">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={allChecked}
                                  onCheckedChange={(checked) => toggleAllPermissions(feature.id, checked)}
                                />
                                <div>
                                  <p className="font-medium text-sm">{feature.name}</p>
                                  <p className="text-xs text-gray-500">{feature.description}</p>
                                </div>
                              </div>
                            </div>
                            <div className="text-center">
                              <Checkbox
                                checked={perms.view}
                                onCheckedChange={() => togglePermission(feature.id, 'view')}
                              />
                            </div>
                            <div className="text-center">
                              <Checkbox
                                checked={perms.edit}
                                onCheckedChange={() => togglePermission(feature.id, 'edit')}
                              />
                            </div>
                            <div className="text-center">
                              <Checkbox
                                checked={perms.delete}
                                onCheckedChange={() => togglePermission(feature.id, 'delete')}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingProfile ? 'Salvar' : 'Criar Perfil'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Exclusão */}
      <AlertDialog open={!!deleteProfileId} onOpenChange={() => setDeleteProfileId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja excluir este perfil? Esta ação não pode ser desfeita.
              Certifique-se de que não há usuários vinculados a este perfil.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Profiles;
